import { useState, useEffect, useCallback } from 'react';
import { 
  RefreshCw, TrendingUp, TrendingDown, Wallet, Users, Building2,
  ChevronRight, ChevronDown, AlertTriangle, Clock, CheckCircle,
  FileText, Bell, CreditCard, ArrowUpRight, ArrowDownRight,
  Calendar, Filter, Search, MoreHorizontal, ExternalLink,
  Send, MousePointer, UserPlus, Shield, DollarSign, XCircle, RotateCcw, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import BrokerageManagement from './BrokerageManagement';

const API = process.env.REACT_APP_BACKEND_URL;

// ============================================================================

// Format currency in INR
const formatINR = (val) => {
  if (!val && val !== 0) return '₹0';
  if (val >= 10000000) return `₹${(val/10000000).toFixed(2)} Cr`;
  if (val >= 100000) return `₹${(val/100000).toFixed(2)} L`;
  return `₹${val.toLocaleString('en-IN')}`;
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short'
  });
};

// Format time ago
const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
};

// Days since
const daysSince = (dateStr) => {
  if (!dateStr) return null;
  return Math.floor((new Date() - new Date(dateStr)) / (1000 * 60 * 60 * 24));
};

// State colors
const STATE_COLORS = {
  DRAFT: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  OPEN: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  FUNDED: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  CLOSED: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  EXITED: 'bg-white/20 text-white border-white/30',
  PAUSED: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
};

const PIPELINE_STATES = ['EOI_PAID', 'DOC_SENT', 'DOC_SIGNED', 'CAPITAL_PAID', 'COMPLETED'];

// Commission Status Colors
const COMMISSION_STATUS_COLORS = {
  PENDING: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  ELIGIBLE: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  INVOICED: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  APPROVED: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  PAID: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
};

// Commissions Tab Content Component
function CommissionsTabContent({ commissionsSummary, brokerInvoices, developerPayments, formatINR, formatDate, onRefresh, token }) {
  const [subTab, setSubTab] = useState('overview');
  const [processingId, setProcessingId] = useState(null);
  const [showDevPaymentModal, setShowDevPaymentModal] = useState(false);
  const [devPaymentForm, setDevPaymentForm] = useState({
    unitId: '',
    amountReceived: '',
    paymentReference: ''
  });

  const handleApproveInvoice = async (invoiceId) => {
    setProcessingId(invoiceId);
    try {
      const res = await fetch(`${API}/api/admin/broker-invoices/${invoiceId}/approve`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes: 'Approved via Operations Console' })
      });
      if (res.ok) {
        toast.success('Invoice approved');
        onRefresh();
      } else {
        toast.error('Failed to approve invoice');
      }
    } catch (e) {
      toast.error('Error approving invoice');
    }
    setProcessingId(null);
  };

  const handleProcessPayout = async (invoiceId) => {
    const ref = prompt('Enter bank transfer reference number:');
    if (!ref) return;
    
    setProcessingId(invoiceId);
    try {
      const res = await fetch(`${API}/api/admin/broker-invoices/${invoiceId}/payout`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ paymentReference: ref })
      });
      if (res.ok) {
        toast.success('Payout processed');
        onRefresh();
      } else {
        toast.error('Failed to process payout');
      }
    } catch (e) {
      toast.error('Error processing payout');
    }
    setProcessingId(null);
  };

  const handleRecordDevPayment = async () => {
    if (!devPaymentForm.unitId || !devPaymentForm.amountReceived || !devPaymentForm.paymentReference) {
      toast.error('Please fill all fields');
      return;
    }
    
    try {
      const res = await fetch(`${API}/api/admin/developer-payments`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          unitId: devPaymentForm.unitId,
          amountReceived: parseFloat(devPaymentForm.amountReceived),
          paymentReference: devPaymentForm.paymentReference,
          developerPaymentId: `DEVPAY_${Date.now()}`
        })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Payment recorded. ${data.commissionsUpdated} commissions now eligible.`);
        setShowDevPaymentModal(false);
        setDevPaymentForm({ unitId: '', amountReceived: '', paymentReference: '' });
        onRefresh();
      } else {
        toast.error('Failed to record payment');
      }
    } catch (e) {
      toast.error('Error recording payment');
    }
  };

  const pendingInvoices = brokerInvoices.filter(inv => inv.status === 'SUBMITTED');
  const approvedInvoices = brokerInvoices.filter(inv => inv.status === 'APPROVED');
  const paidInvoices = brokerInvoices.filter(inv => inv.status === 'PAID');

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="bg-yellow-500/10 border border-yellow-500/30 p-4">
          <p className="text-xs text-yellow-400 uppercase">Pending</p>
          <p className="text-xl font-mono text-yellow-400">
            {formatINR(commissionsSummary?.totals?.pendingAmount || 0)}
          </p>
          <p className="text-xs text-gray-500">{commissionsSummary?.byStatus?.PENDING?.count || 0} records</p>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/30 p-4">
          <p className="text-xs text-blue-400 uppercase">Eligible</p>
          <p className="text-xl font-mono text-blue-400">
            {formatINR(commissionsSummary?.totals?.eligibleAmount || 0)}
          </p>
          <p className="text-xs text-gray-500">{commissionsSummary?.byStatus?.ELIGIBLE?.count || 0} records</p>
        </div>
        <div className="bg-purple-500/10 border border-purple-500/30 p-4">
          <p className="text-xs text-purple-400 uppercase">Invoiced</p>
          <p className="text-xl font-mono text-purple-400">
            {formatINR(commissionsSummary?.byStatus?.INVOICED?.amount || 0)}
          </p>
          <p className="text-xs text-gray-500">{pendingInvoices.length} pending review</p>
        </div>
        <div className="bg-emerald-500/10 border border-emerald-500/30 p-4">
          <p className="text-xs text-emerald-400 uppercase">Paid Out</p>
          <p className="text-xl font-mono text-emerald-400">
            {formatINR(commissionsSummary?.totals?.paidAmount || 0)}
          </p>
          <p className="text-xs text-gray-500">{paidInvoices.length} invoices</p>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-2">
        {['overview', 'invoices', 'dev-payments'].map(tab => (
          <button
            key={tab}
            onClick={() => setSubTab(tab)}
            className={`px-4 py-2 text-xs uppercase tracking-wide transition-colors ${
              subTab === tab 
                ? 'bg-white/10 text-white' 
                : 'text-gray-500 hover:text-white'
            }`}
          >
            {tab === 'dev-payments' ? 'Developer Payments' : tab}
          </button>
        ))}
        <div className="flex-1" />
        <Button 
          size="sm" 
          variant="outline" 
          className="border-emerald-500/50 text-emerald-400"
          onClick={() => setShowDevPaymentModal(true)}
        >
          + Record Dev Payment
        </Button>
      </div>

      {/* Sub-tab Content */}
      {subTab === 'overview' && (
        <div className="space-y-4">
          {/* Pending Approval */}
          {pendingInvoices.length > 0 && (
            <div>
              <h4 className="text-sm text-white mb-2 flex items-center gap-2">
                <span className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" />
                Invoices Pending Approval ({pendingInvoices.length})
              </h4>
              <div className="space-y-2">
                {pendingInvoices.map(inv => (
                  <div key={inv.invoiceId} className="grid grid-cols-6 gap-4 px-4 py-3 border border-white/5 hover:bg-white/5 items-center">
                    <span className="text-sm text-white">{inv.brokerName || 'Broker'}</span>
                    <span className="text-xs text-gray-400">{inv.invoiceNumber}</span>
                    <span className="text-sm text-emerald-400">{formatINR(inv.totalAmount)}</span>
                    <span className="text-xs text-gray-500">{formatDate(inv.submittedAt)}</span>
                    <span className={`text-xs px-2 py-1 ${inv.amountMatch ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                      {inv.amountMatch ? '✓ Verified' : '⚠ Mismatch'}
                    </span>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="border-emerald-500/50 text-emerald-400"
                        onClick={() => handleApproveInvoice(inv.invoiceId)}
                        disabled={processingId === inv.invoiceId}
                      >
                        {processingId === inv.invoiceId ? '...' : 'Approve'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ready for Payout */}
          {approvedInvoices.length > 0 && (
            <div>
              <h4 className="text-sm text-white mb-2 flex items-center gap-2">
                <span className="w-2 h-2 bg-emerald-500 rounded-full" />
                Ready for Payout ({approvedInvoices.length})
              </h4>
              <div className="space-y-2">
                {approvedInvoices.map(inv => (
                  <div key={inv.invoiceId} className="grid grid-cols-6 gap-4 px-4 py-3 border border-white/5 hover:bg-white/5 items-center">
                    <span className="text-sm text-white">{inv.brokerName || 'Broker'}</span>
                    <span className="text-xs text-gray-400">{inv.invoiceNumber}</span>
                    <span className="text-sm text-emerald-400">{formatINR(inv.grandTotal || inv.totalAmount)}</span>
                    <span className="text-xs text-gray-500">
                      {inv.bankDetails?.bankName} - ***{inv.bankDetails?.accountNumber?.slice(-4)}
                    </span>
                    <span className="text-xs text-gray-400">{inv.bankDetails?.ifscCode}</span>
                    <Button 
                      size="sm" 
                      className="bg-emerald-500 hover:bg-emerald-600 text-black"
                      onClick={() => handleProcessPayout(inv.invoiceId)}
                      disabled={processingId === inv.invoiceId}
                    >
                      {processingId === inv.invoiceId ? '...' : 'Pay Now'}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {pendingInvoices.length === 0 && approvedInvoices.length === 0 && (
            <p className="text-gray-500 text-sm text-center py-8">No pending commission actions</p>
          )}
        </div>
      )}

      {subTab === 'invoices' && (
        <div className="space-y-2">
          <div className="grid grid-cols-7 gap-4 px-4 py-2 text-xs text-gray-500 uppercase">
            <span>Invoice</span>
            <span>Broker</span>
            <span>Amount</span>
            <span>GST</span>
            <span>Status</span>
            <span>Date</span>
            <span>Action</span>
          </div>
          {brokerInvoices.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-8">No invoices yet</p>
          ) : (
            brokerInvoices.map(inv => (
              <div key={inv.invoiceId} className="grid grid-cols-7 gap-4 px-4 py-3 border border-white/5 hover:bg-white/5 items-center">
                <span className="text-sm text-white">{inv.invoiceNumber}</span>
                <span className="text-sm text-gray-400">{inv.brokerName || 'Broker'}</span>
                <span className="text-sm text-emerald-400">{formatINR(inv.totalAmount)}</span>
                <span className="text-sm text-gray-400">{formatINR(inv.gstAmount || 0)}</span>
                <span className={`text-xs px-2 py-1 ${COMMISSION_STATUS_COLORS[inv.status] || 'bg-gray-500/20 text-gray-400'}`}>
                  {inv.status}
                </span>
                <span className="text-xs text-gray-500">{formatDate(inv.submittedAt)}</span>
                <span className="text-xs text-gray-400">
                  {inv.status === 'PAID' && inv.paymentReference ? `Ref: ${inv.paymentReference}` : '-'}
                </span>
              </div>
            ))
          )}
        </div>
      )}

      {subTab === 'dev-payments' && (
        <div className="space-y-2">
          <div className="grid grid-cols-5 gap-4 px-4 py-2 text-xs text-gray-500 uppercase">
            <span>Payment ID</span>
            <span>Unit</span>
            <span>Amount</span>
            <span>Reference</span>
            <span>Date</span>
          </div>
          {developerPayments.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-8">No developer payments recorded</p>
          ) : (
            developerPayments.map(payment => (
              <div key={payment.paymentId} className="grid grid-cols-5 gap-4 px-4 py-3 border border-white/5 hover:bg-white/5 items-center">
                <span className="text-sm text-white font-mono">{payment.paymentId}</span>
                <span className="text-sm text-gray-400">{payment.unitId}</span>
                <span className="text-sm text-emerald-400">{formatINR(payment.amountReceived)}</span>
                <span className="text-sm text-gray-400">{payment.paymentReference}</span>
                <span className="text-xs text-gray-500">{formatDate(payment.createdAt)}</span>
              </div>
            ))
          )}
        </div>
      )}

      {/* Developer Payment Modal */}
      {showDevPaymentModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-[#0A0A0A] border border-white/10 p-6 w-full max-w-md">
            <h3 className="text-lg text-white mb-4">Record Developer Payment</h3>
            <p className="text-sm text-gray-400 mb-4">
              Recording a payment will mark all PENDING commissions for this unit as ELIGIBLE.
            </p>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-gray-500 uppercase">Unit ID</label>
                <input 
                  type="text"
                  value={devPaymentForm.unitId}
                  onChange={(e) => setDevPaymentForm({...devPaymentForm, unitId: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 text-white px-3 py-2 mt-1"
                  placeholder="UNIT_PROJ_XXX"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase">Amount Received (₹)</label>
                <input 
                  type="number"
                  value={devPaymentForm.amountReceived}
                  onChange={(e) => setDevPaymentForm({...devPaymentForm, amountReceived: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 text-white px-3 py-2 mt-1"
                  placeholder="50000"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase">Bank Reference</label>
                <input 
                  type="text"
                  value={devPaymentForm.paymentReference}
                  onChange={(e) => setDevPaymentForm({...devPaymentForm, paymentReference: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 text-white px-3 py-2 mt-1"
                  placeholder="NEFT/IMPS reference"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button 
                variant="outline" 
                className="flex-1 border-white/10"
                onClick={() => setShowDevPaymentModal(false)}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-black"
                onClick={handleRecordDevPayment}
              >
                Record Payment
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// ASSIGNMENT FUNNEL COMPONENT
// ============================================================================
function AssignmentFunnel() {
  const [timePeriod, setTimePeriod] = useState('30');
  const [activeTab, setActiveTab] = useState('overview');
  const [showDetails, setShowDetails] = useState(false);
  const [funnelData, setFunnelData] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(true);

  useEffect(() => {
    const fetchFunnel = async () => {
      setFunnelLoading(true);
      try {
        const period = timePeriod === 'all' ? 3650 : parseInt(timePeriod);
        const res = await fetch(`${API}/api/admin/operations/funnel?period=${period}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.ok) {
          const data = await res.json();
          setFunnelData({
            overview: {
              sent: data.funnel?.sent || 0,
              clicked: data.funnel?.clicked || 0,
              registered: data.funnel?.registered || 0,
              kycDone: data.funnel?.kyc || 0,
              eoiPaid: data.funnel?.eoiPaid || 0,
              expired: 0,
              resent: 0,
            },
            byBroker: (data.brokerBreakdown || []).map(b => ({
              brokerId: b.brokerId,
              name: b.brokerName,
              sent: b.sent || 0,
              clicked: b.clicked || 0,
              registered: b.registered || 0,
              kycDone: b.kyc || 0,
              eoiPaid: b.eoiPaid || 0,
              expired: 0,
              resent: 0,
            })),
            details: [],
          });
        } else {
          setFunnelData(null);
        }
      } catch {
        setFunnelData(null);
      } finally {
        setFunnelLoading(false);
      }
    };
    fetchFunnel();
  }, [timePeriod]);

  if (!funnelData) return null;

  const { overview, byBroker, details } = funnelData;

  // Calculate conversion rates
  const clickRate = overview.sent > 0 ? ((overview.clicked / overview.sent) * 100).toFixed(1) : 0;
  const regRate = overview.clicked > 0 ? ((overview.registered / overview.clicked) * 100).toFixed(1) : 0;
  const kycRate = overview.registered > 0 ? ((overview.kycDone / overview.registered) * 100).toFixed(1) : 0;
  const eoiRate = overview.kycDone > 0 ? ((overview.eoiPaid / overview.kycDone) * 100).toFixed(1) : 0;
  const overallConversion = overview.sent > 0 ? ((overview.eoiPaid / overview.sent) * 100).toFixed(1) : 0;

  const funnelStages = [
    { key: 'sent', label: 'Sent', value: overview.sent, icon: Send, color: 'text-blue-400', bg: 'bg-blue-500' },
    { key: 'clicked', label: 'Clicked', value: overview.clicked, rate: clickRate, icon: MousePointer, color: 'text-cyan-400', bg: 'bg-cyan-500' },
    { key: 'registered', label: 'Registered', value: overview.registered, rate: regRate, icon: UserPlus, color: 'text-purple-400', bg: 'bg-purple-500' },
    { key: 'kycDone', label: 'KYC Done', value: overview.kycDone, rate: kycRate, icon: Shield, color: 'text-amber-400', bg: 'bg-amber-500' },
    { key: 'eoiPaid', label: 'EOI Paid', value: overview.eoiPaid, rate: eoiRate, icon: DollarSign, color: 'text-emerald-400', bg: 'bg-emerald-500' },
  ];

  const timePeriodOptions = [
    { value: '30', label: 'Last 30 Days' },
    { value: '60', label: 'Last 60 Days' },
    { value: '90', label: 'Last 90 Days' },
    { value: 'all', label: 'All Time' },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'text-emerald-400 bg-emerald-500/20';
      case 'EXPIRED': return 'text-red-400 bg-red-500/20';
      case 'RESENT': return 'text-amber-400 bg-amber-500/20';
      default: return 'text-gray-400 bg-gray-500/20';
    }
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case 'SENT': return 'text-blue-400';
      case 'CLICKED': return 'text-cyan-400';
      case 'REGISTERED': return 'text-purple-400';
      case 'KYC_DONE': return 'text-amber-400';
      case 'EOI_PAID': return 'text-emerald-400';
      default: return 'text-gray-400';
    }
  };

  return (
    <div className="border border-white/10 bg-white/[0.02]" data-testid="assignment-funnel">
      {/* Header */}
      <div className="px-6 py-3 border-b border-white/10 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <span className="text-xs text-gray-500 uppercase tracking-widest">Assignment Funnel</span>
          <div className="flex border border-white/10">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-3 py-1 text-xs transition-colors ${
                activeTab === 'overview' ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-white'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('byBroker')}
              className={`px-3 py-1 text-xs transition-colors ${
                activeTab === 'byBroker' ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-white'
              }`}
            >
              By Broker
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={timePeriod}
            onChange={(e) => setTimePeriod(e.target.value)}
            className="bg-black border border-white/10 text-gray-400 text-xs px-3 py-1.5 focus:outline-none focus:border-white/30"
          >
            {timePeriodOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'overview' ? (
          <>
            {/* Funnel Visualization */}
            <div className="flex items-stretch justify-between gap-2 mb-6">
              {funnelStages.map((stage, idx) => {
                const Icon = stage.icon;
                const widthPercent = overview.sent > 0 ? (stage.value / overview.sent) * 100 : 0;
                return (
                  <div key={stage.key} className="flex-1 flex flex-col items-center">
                    <div className="relative w-full">
                      {/* Stage box */}
                      <div className={`border border-white/10 bg-white/[0.02] p-4 text-center transition-all hover:bg-white/5`}>
                        <Icon className={`w-5 h-5 ${stage.color} mx-auto mb-2`} />
                        <p className={`text-2xl font-mono ${stage.color}`}>{stage.value}</p>
                        <p className="text-xs text-gray-500 mt-1">{stage.label}</p>
                        {stage.rate && (
                          <p className="text-xs text-gray-600 mt-1">↓ {stage.rate}%</p>
                        )}
                      </div>
                      {/* Funnel bar below */}
                      <div className="h-1 bg-white/5 mt-2">
                        <div 
                          className={`h-full ${stage.bg} transition-all duration-500`}
                          style={{ width: `${widthPercent}%` }}
                        />
                      </div>
                    </div>
                    {/* Arrow connector */}
                    {idx < funnelStages.length - 1 && (
                      <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 text-gray-600">
                        →
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Summary Stats */}
            <div className="flex items-center justify-between border-t border-white/10 pt-4">
              <div className="flex items-center gap-6">
                <span className="flex items-center gap-2 text-sm">
                  <XCircle className="w-4 h-4 text-red-400" />
                  <span className="text-red-400">{overview.expired}</span>
                  <span className="text-gray-500">Expired</span>
                </span>
                <span className="flex items-center gap-2 text-sm">
                  <RotateCcw className="w-4 h-4 text-amber-400" />
                  <span className="text-amber-400">{overview.resent}</span>
                  <span className="text-gray-500">Resent</span>
                </span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-400">
                  Overall Conversion: <span className="text-emerald-400 font-mono">{overallConversion}%</span>
                </span>
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="text-xs text-gray-500 hover:text-white flex items-center gap-1"
                >
                  {showDetails ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  {showDetails ? 'Hide' : 'View'} Details
                </button>
              </div>
            </div>

            {/* Details Table */}
            {showDetails && details && details.length > 0 && (
              <div className="mt-4 border border-white/10">
                <div className="grid grid-cols-7 gap-2 px-4 py-2 bg-white/5 text-xs text-gray-500 uppercase">
                  <span>Broker</span>
                  <span>Investor</span>
                  <span>Unit</span>
                  <span>Links</span>
                  <span>Status</span>
                  <span>Stage</span>
                  <span>Action</span>
                </div>
                {details.slice(0, 10).map((item, idx) => (
                  <div key={idx} className="grid grid-cols-7 gap-2 px-4 py-3 border-t border-white/5 text-sm items-center">
                    <span className="text-gray-400">{item.broker}</span>
                    <span className="text-white">{item.investor}</span>
                    <span className="text-gray-500">{item.unit}</span>
                    <span className="text-gray-500 font-mono">{item.links?.length || 1}</span>
                    <span className={`text-xs px-2 py-0.5 inline-block w-fit ${getStatusColor(item.status)}`}>
                      {item.status}
                    </span>
                    <span className={`text-xs ${getStageColor(item.stage)}`}>{item.stage.replace('_', ' ')}</span>
                    <button 
                      onClick={() => setSelectedAssignment(item)}
                      className="text-xs text-gray-500 hover:text-white flex items-center gap-1"
                    >
                      <Eye className="w-3 h-3" /> View
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          /* By Broker Tab */
          <div className="border border-white/10">
            <div className="grid grid-cols-8 gap-2 px-4 py-2 bg-white/5 text-xs text-gray-500 uppercase">
              <span className="col-span-2">Broker</span>
              <span className="text-center">Sent</span>
              <span className="text-center">Clicked</span>
              <span className="text-center">Registered</span>
              <span className="text-center">KYC</span>
              <span className="text-center">EOI Paid</span>
              <span className="text-center">Conv. %</span>
            </div>
            {byBroker.map((broker, idx) => {
              const brokerConv = broker.sent > 0 ? ((broker.eoiPaid / broker.sent) * 100).toFixed(1) : 0;
              return (
                <div key={idx} className="grid grid-cols-8 gap-2 px-4 py-3 border-t border-white/5 text-sm items-center">
                  <span className="col-span-2 text-white">{broker.name}</span>
                  <span className="text-center text-blue-400 font-mono">{broker.sent}</span>
                  <span className="text-center text-cyan-400 font-mono">{broker.clicked}</span>
                  <span className="text-center text-purple-400 font-mono">{broker.registered}</span>
                  <span className="text-center text-amber-400 font-mono">{broker.kycDone}</span>
                  <span className="text-center text-emerald-400 font-mono">{broker.eoiPaid}</span>
                  <span className={`text-center font-mono ${parseFloat(brokerConv) >= 30 ? 'text-emerald-400' : parseFloat(brokerConv) >= 20 ? 'text-amber-400' : 'text-red-400'}`}>
                    {brokerConv}%
                  </span>
                </div>
              );
            })}
            {/* Summary row */}
            <div className="grid grid-cols-8 gap-2 px-4 py-3 border-t border-white/10 bg-white/5 text-sm font-medium">
              <span className="col-span-2 text-gray-400">TOTAL</span>
              <span className="text-center text-blue-400 font-mono">{overview.sent}</span>
              <span className="text-center text-cyan-400 font-mono">{overview.clicked}</span>
              <span className="text-center text-purple-400 font-mono">{overview.registered}</span>
              <span className="text-center text-amber-400 font-mono">{overview.kycDone}</span>
              <span className="text-center text-emerald-400 font-mono">{overview.eoiPaid}</span>
              <span className="text-center text-emerald-400 font-mono">{overallConversion}%</span>
            </div>
          </div>
        )}
      </div>

      {/* Assignment Detail Modal */}
      {selectedAssignment && (
        <AssignmentDetailModal 
          assignment={selectedAssignment} 
          onClose={() => setSelectedAssignment(null)}
        />
      )}
    </div>
  );
}

// ============================================================================
// ASSIGNMENT DETAIL MODAL
// ============================================================================
function AssignmentDetailModal({ assignment, onClose }) {
  const [resending, setResending] = useState(false);
  const [copySuccess, setCopySuccess] = useState(null);

  if (!assignment) return null;

  const handleResendLink = async () => {
    setResending(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast.success(`New link sent to ${assignment.email}`);
    setResending(false);
    // In real implementation, this would call the API and refresh the data
  };

  const handleCopyLink = (token) => {
    const link = `${window.location.origin}/i/${token}`;
    navigator.clipboard.writeText(link);
    setCopySuccess(token);
    setTimeout(() => setCopySuccess(null), 2000);
  };

  const getEventIcon = (event) => {
    switch (event) {
      case 'LINK_SENT': return <Send className="w-4 h-4 text-blue-400" />;
      case 'LINK_CLICKED': return <MousePointer className="w-4 h-4 text-cyan-400" />;
      case 'LINK_EXPIRED': return <XCircle className="w-4 h-4 text-red-400" />;
      case 'LINK_RESENT': return <RotateCcw className="w-4 h-4 text-amber-400" />;
      case 'REGISTERED': return <UserPlus className="w-4 h-4 text-purple-400" />;
      case 'KYC_COMPLETED': return <Shield className="w-4 h-4 text-amber-400" />;
      case 'EOI_PAID': return <DollarSign className="w-4 h-4 text-emerald-400" />;
      default: return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLinkStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'text-emerald-400 bg-emerald-500/20';
      case 'USED': return 'text-blue-400 bg-blue-500/20';
      case 'EXPIRED': return 'text-red-400 bg-red-500/20';
      default: return 'text-gray-400 bg-gray-500/20';
    }
  };

  const canResend = assignment.status === 'EXPIRED' || 
                    (assignment.links && assignment.links.every(l => l.status === 'EXPIRED'));

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <h2 className="text-lg text-white font-light">Assignment Detail</h2>
            <p className="text-xs text-gray-500 mt-1">{assignment.assignmentId}</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-white transition-colors"
          >
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Investor Info */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-xs text-gray-500 uppercase mb-2">Investor</p>
              <p className="text-white font-medium">{assignment.investor}</p>
              <p className="text-sm text-gray-400 mt-1">{assignment.email}</p>
              <p className="text-sm text-gray-500">{assignment.phone}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase mb-2">Assignment</p>
              <p className="text-white">{assignment.unit}</p>
              <p className="text-sm text-gray-400">{assignment.project}</p>
              <p className="text-sm text-gray-500">Broker: {assignment.broker}</p>
            </div>
          </div>

          {/* Current Status */}
          <div className="flex items-center gap-4 p-4 border border-white/10 bg-white/[0.02]">
            <div className="flex-1">
              <p className="text-xs text-gray-500 uppercase mb-1">Current Stage</p>
              <p className={`text-lg font-medium ${
                assignment.stage === 'EOI_PAID' ? 'text-emerald-400' :
                assignment.stage === 'KYC_DONE' ? 'text-amber-400' :
                assignment.stage === 'REGISTERED' ? 'text-purple-400' :
                assignment.stage === 'CLICKED' ? 'text-cyan-400' :
                'text-gray-400'
              }`}>
                {assignment.stage.replace('_', ' ')}
              </p>
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-500 uppercase mb-1">Status</p>
              <span className={`text-sm px-3 py-1 ${
                assignment.status === 'ACTIVE' ? 'text-emerald-400 bg-emerald-500/20' :
                assignment.status === 'EXPIRED' ? 'text-red-400 bg-red-500/20' :
                'text-amber-400 bg-amber-500/20'
              }`}>
                {assignment.status}
              </span>
            </div>
            {assignment.amount && (
              <div className="flex-1 text-right">
                <p className="text-xs text-gray-500 uppercase mb-1">Amount</p>
                <p className="text-lg font-mono text-white">{formatINR(assignment.amount)}</p>
              </div>
            )}
          </div>

          {/* Links History */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-gray-500 uppercase">Links Sent ({assignment.links?.length || 0})</p>
              {canResend && (
                <button
                  onClick={handleResendLink}
                  disabled={resending}
                  className="flex items-center gap-2 px-3 py-1.5 text-xs border border-amber-500/50 text-amber-400 hover:bg-amber-500/10 disabled:opacity-50"
                >
                  <RotateCcw className={`w-3 h-3 ${resending ? 'animate-spin' : ''}`} />
                  {resending ? 'Sending...' : 'Resend Link'}
                </button>
              )}
            </div>
            <div className="border border-white/10">
              {assignment.links && assignment.links.map((link, idx) => (
                <div key={link.linkId} className={`p-3 ${idx > 0 ? 'border-t border-white/5' : ''}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-mono text-gray-500">#{idx + 1}</span>
                      <span className={`text-xs px-2 py-0.5 ${getLinkStatusColor(link.status)}`}>
                        {link.status}
                      </span>
                    </div>
                    <button
                      onClick={() => handleCopyLink(link.token)}
                      className="text-xs text-gray-500 hover:text-white flex items-center gap-1"
                    >
                      {copySuccess === link.token ? (
                        <><CheckCircle className="w-3 h-3 text-emerald-400" /> Copied!</>
                      ) : (
                        <><ExternalLink className="w-3 h-3" /> Copy Link</>
                      )}
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-xs">
                    <div>
                      <span className="text-gray-600">Created:</span>
                      <span className="text-gray-400 ml-2">{formatDateTime(link.createdAt)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Expires:</span>
                      <span className="text-gray-400 ml-2">{formatDateTime(link.expiresAt)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Clicked:</span>
                      <span className={`ml-2 ${link.clickedAt ? 'text-cyan-400' : 'text-gray-600'}`}>
                        {link.clickedAt ? formatDateTime(link.clickedAt) : 'Not clicked'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Journey Timeline */}
          <div>
            <p className="text-xs text-gray-500 uppercase mb-3">Journey Timeline</p>
            <div className="border border-white/10 p-4">
              <div className="space-y-4">
                {assignment.timeline && assignment.timeline.map((event, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-0.5">
                      {getEventIcon(event.event)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-white">{event.event.replace(/_/g, ' ')}</p>
                        <p className="text-xs text-gray-500">{formatDateTime(event.timestamp)}</p>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{event.details}</p>
                    </div>
                    {idx < assignment.timeline.length - 1 && (
                      <div className="absolute left-[1.35rem] top-6 bottom-0 w-px bg-white/10" />
                    )}
                  </div>
                ))}
                {(!assignment.timeline || assignment.timeline.length === 0) && (
                  <p className="text-sm text-gray-500 text-center py-4">No timeline events</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-white/10 text-gray-400 hover:text-white hover:border-white/20"
          >
            Close
          </button>
          {assignment.stage === 'EOI_PAID' && (
            <button className="px-4 py-2 text-sm bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/30">
              View Investment
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function OperationsConsole({ onViewUnit, onViewUnitDetail }) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Data states
  const [overview, setOverview] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [brokerCommissions, setBrokerCommissions] = useState(null);
  const [pendingPayouts, setPendingPayouts] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [commissionsSummary, setCommissionsSummary] = useState(null);
  const [brokerInvoices, setBrokerInvoices] = useState([]);
  const [developerPayments, setDeveloperPayments] = useState([]);
  const [pendingCancellations, setPendingCancellations] = useState([]);
  const [processingAction, setProcessingAction] = useState(null);
  
  // UI states
  const [actionTab, setActionTab] = useState('escrow');
  const [expandedUnits, setExpandedUnits] = useState({});
  const [selectedPipelineStage, setSelectedPipelineStage] = useState(null);
  
  const token = localStorage.getItem('token');

  const fetchAllData = useCallback(async () => {
    try {
      setRefreshing(true);
      
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Parallel fetch all data
      const [
        overviewRes,
        brokerageFlowRes,
        commissionsRes,
        payoutsRes,
        transactionsRes,
        investmentsRes,
        projectsRes,
        brokerInvoicesRes,
        developerPaymentsRes,
        pendingCancellationsRes
      ] = await Promise.all([
        fetch(`${API}/api/admin/lifecycle/overview`, { headers }),
        fetch(`${API}/api/ops/brokerage/flow?months=6`, { headers }),
        fetch(`${API}/api/admin/broker-commissions/summary`, { headers }),
        fetch(`${API}/api/admin/pending-payouts`, { headers }),
        fetch(`${API}/api/admin/payments-ledger/transactions?limit=20`, { headers }),
        fetch(`${API}/api/admin/investments/all?limit=200`, { headers }),
        fetch(`${API}/api/admin/projects/all`, { headers }),
        fetch(`${API}/api/admin/broker-invoices`, { headers }),
        fetch(`${API}/api/admin/developer-payments`, { headers }),
        fetch(`${API}/api/admin/pending-actions?type=INVESTMENT_CANCELLATION&status=PENDING`, { headers })
      ]);
      
      const [
        overviewData, brokerageFlowData, commissionsData, payoutsData, 
        transactionsData, investmentsData, projectsData,
        brokerInvoicesData, developerPaymentsData, pendingCancellationsData
      ] = await Promise.all([
          overviewRes.ok ? overviewRes.json() : {},
          brokerageFlowRes.ok ? brokerageFlowRes.json() : { months: [], totals: { inflow: 0, outflow: 0, net: 0 }, breakdown: {} },
          commissionsRes.ok ? commissionsRes.json() : { brokerSummary: [], totals: {} },
          payoutsRes.ok ? payoutsRes.json() : { pendingPayouts: [] },
          transactionsRes.ok ? transactionsRes.json() : { transactions: [] },
          investmentsRes.ok ? investmentsRes.json() : { investments: [] },
          projectsRes.ok ? projectsRes.json() : { projects: [] },
          brokerInvoicesRes.ok ? brokerInvoicesRes.json() : { invoices: [] },
          developerPaymentsRes.ok ? developerPaymentsRes.json() : { payments: [] },
          pendingCancellationsRes.ok ? pendingCancellationsRes.json() : { actions: [] }
        ]);
      
      setOverview(overviewData);
      setMonthlyData({
        months: brokerageFlowData.months || [],
        totals: {
          totalInflow: brokerageFlowData.totals?.inflow || 0,
          totalOutflow: brokerageFlowData.totals?.outflow || 0,
          netFlow: brokerageFlowData.totals?.net || 0
        },
        breakdown: brokerageFlowData.breakdown || {}
      });
      setBrokerCommissions(commissionsData);
      setPendingPayouts(payoutsData);
      setTransactions(transactionsData.transactions || []);
      setInvestments(investmentsData.investments || []);
      setProjects(projectsData.projects || []);
      setCommissionsSummary(commissionsData);
      setBrokerInvoices(brokerInvoicesData.invoices || []);
      setDeveloperPayments(developerPaymentsData.payments || []);
      setPendingCancellations(pendingCancellationsData.actions || []);
      
    } catch (error) {
      console.error('Error fetching operations data:', error);
      toast.error('Failed to load operations data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const toggleUnit = (unitId) => {
    setExpandedUnits(prev => ({ ...prev, [unitId]: !prev[unitId] }));
  };

  // Get action counts
  const getActionCounts = () => {
    const escrowReady = overview?.attentionItems?.filter(i => i.type === 'escrow_release').length || 0;
    const overdueInvestments = investments.filter(inv => {
      if (!['DOC_SENT', 'DOC_SIGNED'].includes(inv.state)) return false;
      const lastChange = inv.stateHistory?.[inv.stateHistory.length - 1];
      return daysSince(lastChange?.timestamp) >= 5;
    }).length;
    const pendingPayoutsCount = pendingPayouts?.summary?.totalCount || 0;
    
    // Commission counts
    const pendingInvoices = brokerInvoices.filter(inv => inv.status === 'SUBMITTED').length;
    const approvedInvoices = brokerInvoices.filter(inv => inv.status === 'APPROVED').length;
    const totalCommissionActions = pendingInvoices + approvedInvoices;
    
    return { escrowReady, overdueInvestments, pendingPayoutsCount, totalCommissionActions, pendingInvoices, approvedInvoices };
  };

  // Get escrow ready units
  const getEscrowReadyUnits = () => {
    return overview?.attentionItems?.filter(i => i.type === 'escrow_release') || [];
  };

  // Get overdue investments
  const getOverdueInvestments = () => {
    return investments.filter(inv => {
      if (!['DOC_SENT', 'DOC_SIGNED'].includes(inv.state)) return false;
      const lastChange = inv.stateHistory?.[inv.stateHistory.length - 1];
      return daysSince(lastChange?.timestamp) >= 3;
    }).sort((a, b) => {
      const aDays = daysSince(a.stateHistory?.[a.stateHistory.length - 1]?.timestamp) || 0;
      const bDays = daysSince(b.stateHistory?.[b.stateHistory.length - 1]?.timestamp) || 0;
      return bDays - aDays;
    });
  };

  // Get units with investments
  const getUnitsWithInvestments = () => {
    // Group investments by unitId
    const investmentsByUnit = {};
    investments.forEach(inv => {
      if (!investmentsByUnit[inv.unitId]) {
        investmentsByUnit[inv.unitId] = [];
      }
      investmentsByUnit[inv.unitId].push(inv);
    });
    
    // Merge with project/unit data
    const allUnits = [];
    projects.forEach(project => {
      (project.units || []).forEach(unit => {
        allUnits.push({
          ...unit,
          projectName: project.name,
          projectId: project.projectId,
          investments: investmentsByUnit[unit.unitId] || []
        });
      });
    });
    
    return allUnits;
  };

  // Calculate transaction breakdown percentages
  const getTransactionBreakdown = () => {
    if (!monthlyData?.months) return [];
    
    const totals = {};
    monthlyData.months.forEach(m => {
      Object.entries(m.breakdown || {}).forEach(([type, amount]) => {
        totals[type] = (totals[type] || 0) + amount;
      });
    });
    
    const total = Object.values(totals).reduce((a, b) => a + b, 0);
    if (total === 0) return [];
    
    const breakdown = Object.entries(totals).map(([type, amount]) => ({
      type: type.replace(/_/g, ' '),
      amount,
      percentage: Math.round((amount / total) * 100)
    }));
    
    return breakdown.sort((a, b) => b.amount - a.amount).slice(0, 5);
  };

  const actionCounts = getActionCounts();

  const handleApproveCancellation = async (actionId) => {
    setProcessingAction(actionId);
    try {
      const res = await fetch(`${API}/api/admin/pending-actions/${actionId}/approve-cancellation`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to approve');
      }
      const data = await res.json();
      toast.success(`Cancellation approved. Refund ${formatINR(data.refundAmount)} processed.`);
      fetchAllData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessingAction(null);
    }
  };

  const handleRejectCancellation = async (actionId) => {
    const reason = prompt('Rejection reason:');
    if (!reason) return;
    setProcessingAction(actionId);
    try {
      const res = await fetch(`${API}/api/admin/pending-actions/${actionId}/reject-cancellation?reason=${encodeURIComponent(reason)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to reject');
      }
      toast.success('Cancellation rejected. Investment restored.');
      fetchAllData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessingAction(null);
    }
  };

  if (loading) {
    return (
      <div className="p-10 flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 text-gray-500 animate-spin" />
      </div>
    );
  }

  const unitStates = ['DRAFT', 'OPEN', 'FUNDED', 'CLOSED', 'EXITED'];

  return (
    <div className="p-8 space-y-8" data-testid="operations-console">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-light text-white mb-1">Operations Console</h1>
          <p className="text-sm text-gray-500">Unified financial and lifecycle management</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-600">Last sync: {new Date().toLocaleTimeString()}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAllData}
            disabled={refreshing}
            className="border-white/10 text-gray-400 hover:text-white"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Portfolio Snapshot */}
      <div className="border border-white/10 bg-white/[0.02]">
        <div className="px-6 py-3 border-b border-white/10">
          <span className="text-xs text-gray-500 uppercase tracking-widest">Portfolio Snapshot</span>
        </div>
        <div className="grid grid-cols-5 divide-x divide-white/10">
          <div className="p-6">
            <p className="text-xs text-gray-500 mb-2">TOTAL DEPLOYED</p>
            <p className="text-2xl font-light text-white">{formatINR(overview?.totalInEscrow || 0)}</p>
            <p className="text-xs text-gray-600 mt-1">{overview?.unitsByState?.FUNDED?.count || 0} funded units</p>
          </div>
          <div className="p-6">
            <p className="text-xs text-gray-500 mb-2">ACTIVE TRANCHE</p>
            <p className="text-2xl font-light text-white">{formatINR(overview?.unitsByState?.OPEN?.totalValue || 0)}</p>
            <p className="text-xs text-gray-600 mt-1">{overview?.unitsByState?.OPEN?.count || 0} open units</p>
          </div>
          <div className="p-6">
            <p className="text-xs text-gray-500 mb-2">ESCROW HELD</p>
            <p className="text-2xl font-light text-emerald-400">{formatINR(overview?.totalInEscrow || 0)}</p>
            <p className="text-xs text-gray-600 mt-1">{actionCounts.escrowReady} pending release</p>
          </div>
          <div className="p-6">
            <p className="text-xs text-gray-500 mb-2">ACTIVE INVESTORS</p>
            <p className="text-2xl font-light text-purple-400">{investments?.length || 0}</p>
            <p className="text-xs text-gray-600 mt-1">current investments</p>
          </div>
          <div className="p-6">
            <p className="text-xs text-gray-500 mb-2">PLATFORM FEES</p>
            <p className="text-2xl font-light text-blue-400">{formatINR(overview?.platformFees || 0)}</p>
            <p className="text-xs text-emerald-500 mt-1 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> MTD
            </p>
          </div>
        </div>
      </div>

      {/* Assignment Funnel - NEW */}
      <AssignmentFunnel />

      {/* Brokerage Flow Chart & Transaction Breakdown */}
      <div className="grid grid-cols-3 gap-6">
        {/* Brokerage Flow Chart */}
        <div className="col-span-2 border border-white/10 bg-white/[0.02]">
          <div className="px-6 py-3 border-b border-white/10 flex justify-between items-center">
            <span className="text-xs text-gray-500 uppercase tracking-widest">Brokerage Flow (Last 6 Months)</span>
            <div className="flex items-center gap-4 text-xs">
              <span className="flex items-center gap-2"><span className="w-3 h-3 bg-emerald-500"></span> Dev Inflow</span>
              <span className="flex items-center gap-2"><span className="w-3 h-3 bg-red-500/50"></span> Broker Outflow</span>
            </div>
          </div>
          <div className="p-6">
            <div className="flex items-end justify-between h-48 gap-4">
              {monthlyData?.months?.map((month, idx) => {
                const maxVal = Math.max(
                  ...monthlyData.months.map(m => Math.max(m.inflow, m.outflow))
                ) || 1;
                const inflowHeight = (month.inflow / maxVal) * 160;
                const outflowHeight = (month.outflow / maxVal) * 160;
                
                return (
                  <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                    <div className="flex-1 w-full flex items-end justify-center gap-1">
                      <div 
                        className="w-5 bg-emerald-500/80 transition-all hover:bg-emerald-400"
                        style={{ height: `${Math.max(inflowHeight, 2)}px` }}
                        title={`Dev Inflow: ${formatINR(month.inflow)}`}
                      />
                      <div 
                        className="w-5 bg-red-500/50 transition-all hover:bg-red-400/50"
                        style={{ height: `${Math.max(outflowHeight, 2)}px` }}
                        title={`Broker Outflow: ${formatINR(month.outflow)}`}
                      />
                    </div>
                    <span className="text-xs text-gray-500">{month.month?.split(' ')[0] || `M${idx+1}`}</span>
                  </div>
                );
              })}
              {(!monthlyData?.months || monthlyData.months.length === 0) && (
                <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
                  No brokerage flow data yet
                </div>
              )}
            </div>
            <div className="mt-6 pt-4 border-t border-white/10 flex justify-between text-sm">
              <span className="text-gray-500">
                Total Dev Inflow: <span className="text-emerald-400">{formatINR(monthlyData?.totals?.totalInflow || 0)}</span>
              </span>
              <span className="text-gray-500">
                Total Broker Outflow: <span className="text-red-400">{formatINR(monthlyData?.totals?.totalOutflow || 0)}</span>
              </span>
              <span className="text-gray-500">
                Net to Platform: <span className="text-white">{formatINR(monthlyData?.totals?.netFlow || 0)}</span>
              </span>
            </div>
          </div>
        </div>

        {/* Transaction Breakdown */}
        <div className="border border-white/10 bg-white/[0.02]">
          <div className="px-6 py-3 border-b border-white/10">
            <span className="text-xs text-gray-500 uppercase tracking-widest">Brokerage Breakdown</span>
          </div>
          <div className="p-6 space-y-4">
            {/* Developer Payments */}
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Developer Paid</span>
                <span className="text-emerald-400">{formatINR(monthlyData?.breakdown?.devPayments || monthlyData?.totals?.totalInflow || 0)}</span>
              </div>
              <div className="h-2 bg-white/5 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
            {/* Broker Commissions */}
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Broker Commissions</span>
                <span className="text-red-400">{formatINR(monthlyData?.breakdown?.brokerPayouts || monthlyData?.totals?.totalOutflow || 0)}</span>
              </div>
              <div className="h-2 bg-white/5 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-red-500/50 to-red-600/50"
                  style={{ width: `${monthlyData?.totals?.totalInflow > 0 ? ((monthlyData?.totals?.totalOutflow || 0) / monthlyData.totals.totalInflow * 100) : 0}%` }}
                />
              </div>
            </div>
            {/* Platform Fees */}
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Platform Retained</span>
                <span className="text-white">{formatINR(monthlyData?.breakdown?.platformFees || monthlyData?.totals?.netFlow || 0)}</span>
              </div>
              <div className="h-2 bg-white/5 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-purple-500 to-purple-600"
                  style={{ width: `${monthlyData?.totals?.totalInflow > 0 ? ((monthlyData?.totals?.netFlow || 0) / monthlyData.totals.totalInflow * 100) : 0}%` }}
                />
              </div>
            </div>
            {(!monthlyData?.totals?.totalInflow && !monthlyData?.totals?.totalOutflow) && (
              <p className="text-gray-500 text-sm text-center py-4">
                Run backfill to populate brokerage flow data
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Unit Pipeline */}
      <div className="border border-white/10 bg-white/[0.02]">
        <div className="px-6 py-3 border-b border-white/10">
          <span className="text-xs text-gray-500 uppercase tracking-widest">Unit Pipeline (Click to Filter)</span>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between gap-4">
            {unitStates.map((state, idx) => {
              const data = overview?.unitsByState?.[state] || { count: 0, totalValue: 0 };
              const isSelected = selectedPipelineStage === state;
              
              return (
                <div key={state} className="flex items-center flex-1">
                  <button
                    onClick={() => setSelectedPipelineStage(isSelected ? null : state)}
                    className={`flex-1 p-4 border transition-all ${
                      isSelected 
                        ? 'border-emerald-500 bg-emerald-500/10' 
                        : 'border-white/10 hover:border-white/20 bg-white/[0.02]'
                    }`}
                  >
                    <div className={`inline-block px-2 py-0.5 text-xs border mb-2 ${STATE_COLORS[state]}`}>
                      {state}
                    </div>
                    <p className="text-2xl font-light text-white">{data.count}</p>
                    <p className="text-xs text-gray-500">{formatINR(data.totalValue)}</p>
                  </button>
                  {idx < unitStates.length - 1 && (
                    <ChevronRight className="w-4 h-4 text-gray-600 mx-2 flex-shrink-0" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Action Center */}
      <div className="border border-white/10 bg-white/[0.02]">
        <div className="px-6 py-3 border-b border-white/10">
          <span className="text-xs text-gray-500 uppercase tracking-widest">Action Center</span>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b border-white/10">
          <button
            onClick={() => setActionTab('escrow')}
            className={`px-6 py-3 text-sm transition-colors ${
              actionTab === 'escrow' 
                ? 'text-white border-b-2 border-emerald-500 bg-white/5' 
                : 'text-gray-500 hover:text-white'
            }`}
          >
            🔴 Escrow Release ({actionCounts.escrowReady})
          </button>
          <button
            onClick={() => setActionTab('overdue')}
            className={`px-6 py-3 text-sm transition-colors ${
              actionTab === 'overdue' 
                ? 'text-white border-b-2 border-emerald-500 bg-white/5' 
                : 'text-gray-500 hover:text-white'
            }`}
          >
            ⚠️ Overdue Docs ({actionCounts.overdueInvestments})
          </button>
          <button
            onClick={() => setActionTab('refunds')}
            className={`px-6 py-3 text-sm transition-colors ${
              actionTab === 'refunds' 
                ? 'text-white border-b-2 border-emerald-500 bg-white/5' 
                : 'text-gray-500 hover:text-white'
            }`}
          >
            💸 Refunds ({pendingPayouts?.investorRefunds?.length || 0})
          </button>
          <button
            onClick={() => setActionTab('brokerage-ssot')}
            className={`px-6 py-3 text-sm transition-colors ${
              actionTab === 'brokerage-ssot' 
                ? 'text-white border-b-2 border-emerald-500 bg-white/5' 
                : 'text-gray-500 hover:text-white'
            }`}
          >
            Brokerage
          </button>
          <button
            onClick={() => setActionTab('cancellations')}
            className={`px-6 py-3 text-sm transition-colors relative ${
              actionTab === 'cancellations' 
                ? 'text-white border-b-2 border-red-500 bg-white/5' 
                : 'text-gray-500 hover:text-white'
            }`}
            data-testid="cancellations-tab"
          >
            Cancellations
            {pendingCancellations.length > 0 && (
              <span className="ml-2 px-1.5 py-0.5 text-[10px] font-mono bg-red-500/20 text-red-400">
                {pendingCancellations.length}
              </span>
            )}
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-4">
          {actionTab === 'escrow' && (
            <div className="space-y-2">
              {getEscrowReadyUnits().length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-8">No escrow pending release</p>
              ) : (
                <>
                  <div className="grid grid-cols-5 gap-4 px-4 py-2 text-xs text-gray-500 uppercase">
                    <span>Unit</span>
                    <span>Escrow Balance</span>
                    <span>Investors</span>
                    <span>T1 Status</span>
                    <span>Action</span>
                  </div>
                  {getEscrowReadyUnits().map((item, idx) => (
                    <div key={idx} className="grid grid-cols-5 gap-4 px-4 py-3 border border-white/5 hover:bg-white/5 items-center">
                      <span className="text-sm text-white">{item.unitName}</span>
                      <span className="text-sm text-emerald-400">{formatINR(item.escrowBalance || 0)}</span>
                      <span className="text-sm text-gray-400">{item.investorCount || 0}</span>
                      <span className="text-xs text-emerald-400">100% ✅</span>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                        onClick={() => onViewUnit && onViewUnit(item.unitId)}
                      >
                        View Lifecycle
                      </Button>
                    </div>
                  ))}
                  <p className="text-xs text-gray-500 mt-4 px-4">
                    💡 To release escrow, navigate to the unit's lifecycle page under All Properties and transition the state.
                  </p>
                </>
              )}
            </div>
          )}

          {actionTab === 'overdue' && (
            <div className="space-y-2">
              {getOverdueInvestments().length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-8">No overdue documents</p>
              ) : (
                <>
                  <div className="grid grid-cols-6 gap-4 px-4 py-2 text-xs text-gray-500 uppercase">
                    <span>Investor</span>
                    <span>Unit</span>
                    <span>Status</span>
                    <span>Days</span>
                    <span>Broker</span>
                    <span>Action</span>
                  </div>
                  {getOverdueInvestments().map((inv, idx) => {
                    const days = daysSince(inv.stateHistory?.[inv.stateHistory.length - 1]?.timestamp);
                    return (
                      <div key={idx} className="grid grid-cols-6 gap-4 px-4 py-3 border border-white/5 hover:bg-white/5 items-center">
                        <span className="text-sm text-white">{inv.investorName || 'Unknown'}</span>
                        <span className="text-sm text-gray-400">{inv.unitName || inv.unitId}</span>
                        <span className={`text-xs ${inv.state === 'DOC_SENT' ? 'text-yellow-400' : 'text-orange-400'}`}>
                          {inv.state?.replace('_', ' ')}
                        </span>
                        <span className={`text-sm ${days >= 7 ? 'text-red-400' : 'text-yellow-400'}`}>
                          {days >= 7 ? '🔴' : '⚠️'} {days}d
                        </span>
                        <span className="text-sm text-gray-400">{inv.brokerName || '-'}</span>
                        <Button size="sm" variant="outline" className="border-white/10 text-gray-400">
                          Remind
                        </Button>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}

          {actionTab === 'refunds' && (
            <div className="space-y-2">
              {(pendingPayouts?.investorRefunds?.length || 0) === 0 ? (
                <p className="text-gray-500 text-sm text-center py-8">No pending refunds</p>
              ) : (
                <>
                  <div className="grid grid-cols-5 gap-4 px-4 py-2 text-xs text-gray-500 uppercase">
                    <span>Type</span>
                    <span>Investor</span>
                    <span>Amount</span>
                    <span>Reason</span>
                    <span>Action</span>
                  </div>
                  {(pendingPayouts?.investorRefunds || []).map((refund, idx) => (
                    <div key={idx} className="grid grid-cols-5 gap-4 px-4 py-3 border border-white/5 hover:bg-white/5 items-center">
                      <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400">
                        REFUND
                      </span>
                      <span className="text-sm text-white">{refund.recipientName || refund.recipientId}</span>
                      <span className="text-sm text-emerald-400">{formatINR(refund.amount)}</span>
                      <span className="text-sm text-gray-400">{refund.reason}</span>
                      <Button size="sm" variant="outline" className="border-emerald-500/50 text-emerald-400">
                        Process
                      </Button>
                    </div>
                  ))}
                  <div className="flex justify-between items-center px-4 py-3 bg-white/5 mt-2">
                    <span className="text-sm text-gray-400">
                      Total: {pendingPayouts?.investorRefunds?.length || 0} refunds
                    </span>
                    <span className="text-sm text-emerald-400 font-medium">
                      {formatINR(pendingPayouts?.summary?.refundsAmount || 0)}
                    </span>
                  </div>
                </>
              )}
            </div>
          )}

          {actionTab === 'brokerage-ssot' && (
            <BrokerageManagement token={token} />
          )}

          {actionTab === 'cancellations' && (
            <div data-testid="cancellations-content">
              {pendingCancellations.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-8">No pending cancellation requests</p>
              ) : (
                <div className="space-y-3">
                  {pendingCancellations.map(action => (
                    <div key={action.actionId} className="border border-red-500/20 bg-red-500/5 p-4" data-testid={`cancellation-card-${action.actionId}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="text-sm text-white font-medium">
                            {action.investorName} — {action.unitName || action.investmentId}
                          </h4>
                          <p className="text-xs text-gray-500 mt-0.5">{action.projectName}</p>
                        </div>
                        <span className="px-2 py-0.5 text-[10px] font-mono bg-yellow-500/20 text-yellow-400">PENDING</span>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mb-3">
                        <div>
                          <p className="text-gray-600">Refund Amount</p>
                          <p className="text-red-400 font-mono">{formatINR(action.refundAmount)}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Original Commitment</p>
                          <p className="text-white font-mono">{formatINR(action.originalCommitment)}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Unit Funding Impact</p>
                          <p className="text-yellow-400 font-mono">
                            {action.unitFundingBefore ?? '—'}% → {action.unitFundingAfter ?? '—'}%
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600">Commission Voided</p>
                          <p className="text-orange-400 font-mono">{formatINR(action.commissionVoided || 0)}</p>
                        </div>
                      </div>

                      <div className="text-xs mb-3 p-2 bg-white/5">
                        <span className="text-gray-600">Reason: </span>
                        <span className="text-gray-300">{action.reason?.replace(/_/g, ' ')}</span>
                        {action.details && <p className="text-gray-500 mt-1">{action.details}</p>}
                      </div>

                      {action.brokerId && (
                        <div className="text-xs mb-3 text-gray-500">
                          Broker: <span className="text-white">{action.brokerName || action.brokerId}</span>
                        </div>
                      )}

                      {action.flags?.includes('RE_ENTRY') && (
                        <div className="text-[10px] px-2 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 mb-3 inline-block">
                          RE-ENTRY: This investor previously cancelled and re-entered this unit
                        </div>
                      )}

                      <div className="flex gap-2 mt-2">
                        <Button
                          size="sm"
                          onClick={() => handleApproveCancellation(action.actionId)}
                          disabled={processingAction === action.actionId}
                          className="bg-red-600 hover:bg-red-700 text-white text-xs"
                          data-testid={`approve-cancel-${action.actionId}`}
                        >
                          {processingAction === action.actionId ? 'Processing...' : 'Approve & Refund'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRejectCancellation(action.actionId)}
                          disabled={processingAction === action.actionId}
                          className="border-white/20 text-gray-400 hover:text-white text-xs"
                          data-testid={`reject-cancel-${action.actionId}`}
                        >
                          Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Broker Commissions & Pending Payouts */}
      <div className="grid grid-cols-2 gap-6">
        {/* Broker Commissions */}
        <div className="border border-white/10 bg-white/[0.02]">
          <div className="px-6 py-3 border-b border-white/10">
            <span className="text-xs text-gray-500 uppercase tracking-widest">Broker Commissions</span>
          </div>
          <div className="divide-y divide-white/5">
            <div className="grid grid-cols-4 gap-4 px-6 py-2 text-xs text-gray-500">
              <span>Broker</span>
              <span className="text-right">Earned</span>
              <span className="text-right">Paid</span>
              <span className="text-right">Due</span>
            </div>
            {brokerCommissions?.brokers?.slice(0, 5).map((broker, idx) => (
              <div key={idx} className="grid grid-cols-4 gap-4 px-6 py-3 text-sm hover:bg-white/5">
                <span className="text-white truncate">{broker.name}</span>
                <span className="text-right text-gray-400">{formatINR(broker.earned)}</span>
                <span className="text-right text-emerald-400">{formatINR(broker.paid)}</span>
                <span className="text-right text-purple-400">{formatINR(broker.pending)}</span>
              </div>
            ))}
            <div className="grid grid-cols-4 gap-4 px-6 py-3 text-sm bg-white/5 font-medium">
              <span className="text-gray-400">TOTAL</span>
              <span className="text-right text-white">{formatINR(brokerCommissions?.totals?.totalEarned)}</span>
              <span className="text-right text-emerald-400">{formatINR(brokerCommissions?.totals?.totalPaid)}</span>
              <span className="text-right text-purple-400">{formatINR(brokerCommissions?.totals?.totalPending)}</span>
            </div>
          </div>
        </div>

        {/* Pending Payouts Summary */}
        <div className="border border-white/10 bg-white/[0.02]">
          <div className="px-6 py-3 border-b border-white/10">
            <span className="text-xs text-gray-500 uppercase tracking-widest">Pending Payouts Summary</span>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-white/5">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-purple-500/20 flex items-center justify-center">
                  <Users className="w-4 h-4 text-purple-400" />
                </div>
                <span className="text-white">Broker Payouts</span>
              </div>
              <div className="text-right">
                <p className="text-white">{pendingPayouts?.summary?.brokerPayoutsCount || 0}</p>
                <p className="text-xs text-emerald-400">{formatINR(pendingPayouts?.summary?.brokerPayoutsAmount || 0)}</p>
              </div>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-white/5">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-500/20 flex items-center justify-center">
                  <CreditCard className="w-4 h-4 text-blue-400" />
                </div>
                <span className="text-white">Investor Refunds</span>
              </div>
              <div className="text-right">
                <p className="text-white">{pendingPayouts?.summary?.refundsCount || 0}</p>
                <p className="text-xs text-emerald-400">{formatINR(pendingPayouts?.summary?.refundsAmount || 0)}</p>
              </div>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-white/5">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-emerald-500/20 flex items-center justify-center">
                  <ArrowUpRight className="w-4 h-4 text-emerald-400" />
                </div>
                <span className="text-white">Exit Payouts</span>
              </div>
              <div className="text-right">
                <p className="text-white">{pendingPayouts?.summary?.exitsCount || 0}</p>
                <p className="text-xs text-emerald-400">{formatINR(pendingPayouts?.summary?.exitsAmount || 0)}</p>
              </div>
            </div>
            <div className="flex justify-between items-center pt-4 border-t border-white/10">
              <span className="text-gray-400 font-medium">TOTAL PENDING</span>
              <div className="text-right">
                <p className="text-xl text-emerald-400">{formatINR(pendingPayouts?.summary?.totalAmount || 0)}</p>
                <p className="text-xs text-gray-500">{pendingPayouts?.summary?.totalCount || 0} payouts</p>
              </div>
            </div>
            <p className="text-xs text-gray-600 text-center mt-2">Payouts are processed individually per property</p>
          </div>
        </div>
      </div>
    </div>
  );
}
